import { MONTHLY_PRICE_LABEL, ANNUAL_PRICE_LABEL } from '../config/pricing';

const MONTHLY_PLAN_KEY = 'monthly';
const ANNUAL_UPFRONT_PLAN_KEY = 'annual_upfront';

export function sanitizePlanId(value) {
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim();
}

function buildBillingPlanCatalog() {
    const monthlyItemPriceId = sanitizePlanId(process.env.REACT_APP_DEFAULT_PLAN_PRICE) || 'beta';
    const annualItemPriceId = sanitizePlanId(process.env.REACT_APP_ANNUAL_UPFRONT_PLAN_PRICE);

    // Keep checkout, landing, and account messaging aligned through one shared plan catalog.
    return {
        [MONTHLY_PLAN_KEY]: {
            key: MONTHLY_PLAN_KEY,
            chargebeeItemPriceId: monthlyItemPriceId,
            autoRenews: true,
            name: 'Monthly Access',
            checkoutName: 'TestCoachAI Monthly Access',
            priceLabel: MONTHLY_PRICE_LABEL,
            badge: 'Flexible',
            description: 'Low-commitment access that renews monthly after the free trial.',
            termSummary: 'Renews monthly until cancelled',
            ctaLabel: 'Choose Monthly',
            features: [
                'Daily Practice with instant feedback',
                'Practice Mistakes that follows completed work',
                'Full-length exam mode for all four components',
                'Parent dashboard with score and timing trends',
                'Email support'
            ]
        },
        [ANNUAL_UPFRONT_PLAN_KEY]: {
            key: ANNUAL_UPFRONT_PLAN_KEY,
            chargebeeItemPriceId: annualItemPriceId,
            autoRenews: false,
            name: '12-Month Access',
            checkoutName: 'TestCoachAI 12-Month Access',
            priceLabel: ANNUAL_PRICE_LABEL,
            badge: 'Best value',
            description: 'Pay once for 12 months of access after the free trial.',
            termSummary: 'One payment for 12 months of access',
            ctaLabel: 'Choose 12 Months',
            features: [
                'Everything in monthly access',
                'Single upfront payment after the free trial',
                'Access continues for 12 months',
                'No ongoing monthly renewal to manage',
                'Parent dashboard and support included'
            ]
        }
    };
}

export function getBillingPlans() {
    return buildBillingPlanCatalog();
}

export function getBillingPlan(planSelection) {
    const requestedPlanId = sanitizePlanId(planSelection);
    const plans = getBillingPlans();

    if (requestedPlanId && plans[requestedPlanId]) {
        return plans[requestedPlanId];
    }

    return Object.values(plans).find((plan) => plan.chargebeeItemPriceId === requestedPlanId) || null;
}

export function hasBillingPlanSelection(planSelection) {
    return Boolean(getBillingPlan(planSelection));
}

export function getConfiguredBillingPlans() {
    return Object.values(getBillingPlans()).filter((plan) => Boolean(plan.chargebeeItemPriceId));
}

export function getDefaultPlanId() {
    const monthlyPlan = getBillingPlans()[MONTHLY_PLAN_KEY];
    return monthlyPlan ? monthlyPlan.key : MONTHLY_PLAN_KEY;
}

export function resolvePlanId(requestedPlanId) {
    const matchedPlan = getBillingPlan(requestedPlanId);
    return matchedPlan ? matchedPlan.key : getDefaultPlanId();
}

export function getPlanDateLabel(subscriptionLike) {
    const subscription = subscriptionLike || {};
    const status = sanitizePlanId(subscription.status).toLowerCase();
    // Support both planKey (new model) and planId (legacy)
    const plan = getBillingPlan(subscription.planKey || subscription.planId);

    if (status === 'trialing') {
        return 'Trial ends on:';
    }

    if (plan && plan.autoRenews === false) {
        return 'Access ends on:';
    }

    if (status === 'non_renewing' || subscription.cancelAtPeriodEnd) {
        return 'Ends on:';
    }

    return 'Renews on:';
}

// Single subscription entry entitlement check — use this when you already have the entry.
export function isEntitlementActive(subscriptionEntry) {
    const subscription = subscriptionEntry || {};
    const status = sanitizePlanId(subscription.status).toLowerCase();
    const now = Date.now();

    if (status === 'active' || status === 'trialing' || status === 'past_due') {
        return true;
    }

    if (status === 'non_renewing') {
        const endTimestamp = subscription.endDate
            ? new Date(subscription.endDate).getTime()
            : (subscription.nextBillingAt ? new Date(subscription.nextBillingAt).getTime() : 0);
        return Boolean(endTimestamp) && now < endTimestamp;
    }

    return false;
}

// Course-aware entitlement check. subscriptions is the user.subscriptions[] array; courseId is optional.
// When courseId is omitted, returns true if any subscription is active (covers single-course scenario).
export function isSubscriptionEntitled(subscriptions, courseId) {
    // Backward compat: if a single subscription-like object is passed, check it directly.
    if (!Array.isArray(subscriptions)) {
        return isEntitlementActive(subscriptions);
    }
    if (subscriptions.length === 0) {
        return false;
    }
    if (!courseId) {
        return subscriptions.some(s => isEntitlementActive(s));
    }
    // Try exact courseId match first (new per-course model)
    const entry = subscriptions.find(
        s => s.courseId && s.courseId.toString() === String(courseId)
    );
    if (entry) return isEntitlementActive(entry);
    // Fall back to legacy entries (no courseId) — covers users migrated from old flat localStorage keys
    const legacyEntry = subscriptions.find(s => !s.courseId);
    return legacyEntry ? isEntitlementActive(legacyEntry) : false;
}

// Fetches billing plans for a specific course from the API.
//
// Throws on failure rather than swallowing it. Callers that gate payment on the authoritative
// course catalog need to tell "this course sells nothing" apart from "we could not ask" — the
// old behaviour returned [] for both, which made a network blip indistinguishable from a real
// empty result. Use fetchCourseBillingPlansSafe() where a best-effort read is genuinely fine.
export async function fetchCourseBillingPlans(courseId, token, { timeoutMs = 8000, retries = 1 } = {}) {
    if (!courseId) return [];

    const url = `${process.env.REACT_APP_API_URL}courses/${encodeURIComponent(courseId)}/plans`;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let lastError = null;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const response = await fetch(url, { headers, signal: controller.signal });
            if (!response.ok) {
                throw new Error(`Course plans request failed with ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            lastError = error;
        } finally {
            clearTimeout(timer);
        }
    }
    throw lastError || new Error('Course plans request failed');
}

// Best-effort variant for surfaces that must never block on billing config (e.g. the public
// landing page, which falls back to the static catalog).
export async function fetchCourseBillingPlansSafe(courseId, token, options) {
    try {
        return await fetchCourseBillingPlans(courseId, token, options);
    } catch {
        return [];
    }
}

// Merges an authoritative API plan over a static catalog entry using an explicit allowlist.
//
// A blanket spread let any API field — including an empty string — clobber static copy, and let
// stale database values dictate the advertised price and renewal terms. Those two now always
// come from the local catalog.
export function mergePlanWithStatic(staticPlan, apiPlan) {
    const base = staticPlan || {};
    const incoming = apiPlan || {};
    const merged = { ...base, key: incoming.key || base.key };

    if (incoming.chargebeeItemPriceId) {
        merged.chargebeeItemPriceId = incoming.chargebeeItemPriceId;
    }
    if (typeof incoming.name === 'string' && incoming.name.trim()) {
        merged.name = incoming.name;
    }
    if (typeof incoming.description === 'string' && incoming.description.trim()) {
        merged.description = incoming.description;
    }

    // priceLabel and autoRenews are deliberately NOT taken from the API.
    return merged;
}
